import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FaCheckCircle, FaStar, FaBuilding, FaLandmark, FaArrowRight,
  FaGlobe, FaIdCard, FaFileAlt, FaSpinner, FaCheck, FaTimes
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import VerificationBadge from '../components/VerificationBadge';
import api from '../utils/api';
import { toast } from 'sonner';

const Verification = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [proofUrl, setProofUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);

  const badgeTypes = [
    {
      id: 'verified',
      name: 'Verified',
      icon: FaCheckCircle,
      color: '#1DA1F2',
      description: 'For notable public figures, celebrities, and influential individuals',
      requirements: ['Public profile', '1000+ followers', 'Notable presence outside TalkinPoint']
    },
    {
      id: 'creator',
      name: 'Creator',
      icon: FaStar,
      color: '#A855F7',
      description: 'For content creators, artists, and influencers',
      requirements: ['Active content creator', '500+ followers', 'Regular posting history']
    },
    {
      id: 'business',
      name: 'Business',
      icon: FaBuilding,
      color: '#6B7280',
      description: 'For registered businesses and brands',
      requirements: ['Registered business', 'Official website', 'Business documentation']
    },
    {
      id: 'official',
      name: 'Official',
      icon: FaCheckCircle,
      color: '#FFD700',
      description: 'For official organizations and institutions',
      requirements: ['Registered organization', 'Official documentation', 'Public recognition']
    },
    {
      id: 'government',
      name: 'Government',
      icon: FaLandmark,
      color: '#6B7280',
      description: 'For government officials and agencies',
      requirements: ['Government affiliation', 'Official ID', 'Agency verification']
    }
  ];

  const categories = [
    'Entertainment', 'Sports', 'Music', 'News & Media', 'Business & Finance',
    'Technology', 'Gaming', 'Fashion & Beauty', 'Food & Lifestyle', 
    'Education', 'Health & Wellness', 'Politics', 'Non-Profit', 'Other'
  ];

  useEffect(() => {
    loadVerificationStatus();
  }, []);

  const loadVerificationStatus = async () => {
    try {
      const response = await api.get('/verification/status');
      setVerificationStatus(response.data);
    } catch (error) {
      console.error('Failed to load verification status:', error);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedBadge || !category || !description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/verification/request', {
        badge_type: selectedBadge,
        category,
        description,
        proof_url: proofUrl || null
      });
      toast.success('Verification badge granted!');
      // Update local user data with badge
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      user.is_verified = true;
      user.verification_badge = selectedBadge;
      user.verification_category = category;
      sessionStorage.setItem('user', JSON.stringify(user));
      loadVerificationStatus();
      setStep(4);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  if (statusLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="grid grid-cols-1 md:grid-cols-12">
          <Sidebar />
          <main className="col-span-12 md:col-span-10 p-6 md:p-10">
            <div className="flex items-center justify-center h-64">
              <FaSpinner className="animate-spin text-4xl text-blue-500" />
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Already verified
  if (verificationStatus?.is_verified) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="grid grid-cols-1 md:grid-cols-12">
          <Sidebar />
          <main className="col-span-12 md:col-span-10 p-6 md:p-10">
            <div className="max-w-2xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl p-8 border border-gray-200 text-center"
              >
                <div className="w-20 h-20 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center mx-auto mb-6">
                  <FaCheck className="text-white text-3xl" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">You're Verified!</h2>
                <p className="text-gray-500 mb-6">
                  Your account has been verified with a {verificationStatus.badge_type} badge in the {verificationStatus.category} category.
                </p>
                <div className="flex items-center justify-center space-x-2 p-4 bg-gray-50 rounded-lg">
                  <span className="text-gray-700 font-medium">Your Badge:</span>
                  <VerificationBadge badgeType={verificationStatus.badge_type} size="lg" />
                </div>
              </motion.div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Pending request
  if (verificationStatus?.pending_request) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="grid grid-cols-1 md:grid-cols-12">
          <Sidebar />
          <main className="col-span-12 md:col-span-10 p-6 md:p-10">
            <div className="max-w-2xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl p-8 border border-gray-200 text-center"
              >
                <div className="w-20 h-20 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-6">
                  <FaSpinner className="text-yellow-500 text-3xl animate-spin" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Request Pending</h2>
                <p className="text-gray-500 mb-6">
                  Your verification request is being reviewed. This usually takes 1-3 business days.
                </p>
                <div className="bg-gray-50 rounded-lg p-4 text-left">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Badge Type:</span>
                      <p className="font-medium text-gray-900 capitalize">{verificationStatus.pending_request.badge_type}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Category:</span>
                      <p className="font-medium text-gray-900">{verificationStatus.pending_request.category}</p>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-500">Submitted:</span>
                      <p className="font-medium text-gray-900">
                        {new Date(verificationStatus.pending_request.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="grid grid-cols-1 md:grid-cols-12">
        <Sidebar />
        
        <main className="col-span-12 md:col-span-10 p-6 md:p-10">
          <div className="max-w-3xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900">Get Verified</h1>
              <p className="text-gray-500 mt-2">Apply for a verification badge on TalkinPoint</p>
            </div>

            {/* Progress Steps */}
            <div className="flex items-center justify-between mb-8">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    step >= s 
                      ? 'bg-gradient-to-r from-[#4A90E2] to-[#A855F7] text-white' 
                      : 'bg-gray-200 text-gray-500'
                  }`}>
                    {step > s ? <FaCheck /> : s}
                  </div>
                  {s < 3 && (
                    <div className={`w-24 md:w-32 h-1 mx-2 ${step > s ? 'bg-gradient-to-r from-[#4A90E2] to-[#A855F7]' : 'bg-gray-200'}`} />
                  )}
                </div>
              ))}
            </div>

            {/* Step 1: Choose Badge Type */}
            {step === 1 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-4"
              >
                <h2 className="text-xl font-bold text-gray-900 mb-4">Choose Your Badge Type</h2>
                
                {badgeTypes.map((badge) => (
                  <motion.button
                    key={badge.id}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    onClick={() => setSelectedBadge(badge.id)}
                    className={`w-full p-6 rounded-xl border-2 text-left transition-all ${
                      selectedBadge === badge.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start space-x-4">
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: `${badge.color}20` }}
                      >
                        <badge.icon className="text-xl" style={{ color: badge.color }} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h3 className="font-bold text-gray-900">{badge.name}</h3>
                          <VerificationBadge badgeType={badge.id} size="sm" showTooltip={false} />
                        </div>
                        <p className="text-gray-500 text-sm mt-1">{badge.description}</p>
                        <div className="flex flex-wrap gap-2 mt-3">
                          {badge.requirements.map((req, i) => (
                            <span key={`req-${i}-${req}`} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                              {req}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        selectedBadge === badge.id ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                      }`}>
                        {selectedBadge === badge.id && <FaCheck className="text-white text-xs" />}
                      </div>
                    </div>
                  </motion.button>
                ))}

                <button
                  onClick={() => selectedBadge && setStep(2)}
                  disabled={!selectedBadge}
                  className={`w-full py-4 rounded-xl font-bold text-white flex items-center justify-center space-x-2 ${
                    selectedBadge 
                      ? 'bg-gradient-to-r from-[#4A90E2] to-[#A855F7] hover:opacity-90' 
                      : 'bg-gray-300 cursor-not-allowed'
                  }`}
                >
                  <span>Continue</span>
                  <FaArrowRight />
                </button>
              </motion.div>
            )}

            {/* Step 2: Category & Description */}
            {step === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <h2 className="text-xl font-bold text-gray-900 mb-4">Tell Us About Yourself</h2>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Category *</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select a category</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Why should you be verified? *
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Tell us about yourself, your work, and why you should receive a verification badge..."
                    rows={5}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    maxLength={500}
                  />
                  <p className="text-gray-400 text-sm mt-1">{description.length}/500 characters</p>
                </div>

                <div className="flex space-x-4">
                  <button
                    onClick={() => setStep(1)}
                    className="flex-1 py-4 rounded-xl font-bold text-gray-700 bg-gray-100 hover:bg-gray-200"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => category && description && setStep(3)}
                    disabled={!category || !description}
                    className={`flex-1 py-4 rounded-xl font-bold text-white flex items-center justify-center space-x-2 ${
                      category && description
                        ? 'bg-gradient-to-r from-[#4A90E2] to-[#A855F7] hover:opacity-90' 
                        : 'bg-gray-300 cursor-not-allowed'
                    }`}
                  >
                    <span>Continue</span>
                    <FaArrowRight />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Proof & Submit */}
            {step === 3 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <h2 className="text-xl font-bold text-gray-900 mb-4">Verify Your Identity</h2>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <FaGlobe className="text-blue-500 mt-1" />
                    <div>
                      <h4 className="font-medium text-blue-900">Link to Official Website (Optional)</h4>
                      <p className="text-blue-700 text-sm">Provide a link to your official website or social media profile that mentions your TalkinPoint account.</p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Proof URL (Optional)
                  </label>
                  <input
                    type="url"
                    value={proofUrl}
                    onChange={(e) => setProofUrl(e.target.value)}
                    placeholder="https://your-website.com"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Summary */}
                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="font-bold text-gray-900 mb-4">Review Your Application</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Badge Type:</span>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium capitalize">{selectedBadge}</span>
                        <VerificationBadge badgeType={selectedBadge} size="sm" showTooltip={false} />
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Category:</span>
                      <span className="font-medium">{category}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Description:</span>
                      <p className="text-gray-900 mt-1">{description}</p>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-4">
                  <button
                    onClick={() => setStep(2)}
                    className="flex-1 py-4 rounded-xl font-bold text-gray-700 bg-gray-100 hover:bg-gray-200"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="flex-1 py-4 rounded-xl font-bold text-white bg-gradient-to-r from-[#4A90E2] to-[#A855F7] hover:opacity-90 flex items-center justify-center space-x-2"
                  >
                    {loading ? (
                      <FaSpinner className="animate-spin" />
                    ) : (
                      <>
                        <span>Submit Application</span>
                        <FaCheck />
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 4: Success - Badge Granted */}
            {step === 4 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-12"
              >
                <div className="w-20 h-20 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center mx-auto mb-6">
                  <FaCheck className="text-white text-3xl" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">You're Verified!</h2>
                <p className="text-gray-500 mb-4">
                  Your <span className="font-bold capitalize">{selectedBadge}</span> badge has been granted. It will now appear next to your name across TalkinPoint.
                </p>
                <div className="flex items-center justify-center space-x-2 mb-8 p-4 bg-gray-50 rounded-lg">
                  <span className="text-gray-700 font-medium">Your Badge:</span>
                  <VerificationBadge badgeType={selectedBadge} size="lg" />
                </div>
                <button
                  onClick={() => navigate('/profile')}
                  className="px-8 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-[#4A90E2] to-[#A855F7]"
                >
                  View Your Profile
                </button>
              </motion.div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Verification;
